import { apis } from '@doltech/core/lib/api/api.config';
import { apiCacheKeys, apiUrls } from '@doltech/core/lib/api/api.urls';
import { queryClient } from '@doltech/core/lib/hooks/withReactQueryConfig';
import { components as onlineTestDtoComponents } from '@doltech/dto';
import { components } from '@doltech/pageManagement/dto';
import { showToast } from '@doltech/ui/lib/figma/Toast';
import { stringifyEditorFields } from '@doltech/utils/lib/object';
import { noop } from 'lodash';
import { apiPublicUrls } from '@doltech/core/lib/api/api.public-urls';

const transformDetail = (data) => {
  if (data?.topic?.id) {
    (data as any).topicId = data?.topic?.id;
  }
  return data;
};

const processUpdateOrPublishDailyNews = async ({ id, payload, onSuccess = noop, apiFn }) => {
  try {
    stringifyEditorFields(payload, ['content']);

    const res = await apiFn({
      id,
      payload,
    });
    queryClient.setQueryData(
      [apiCacheKeys.onlineTest.GET_DAILY_LEARNING_NEWS_DETAIL, id],
      transformDetail(res)
    );
    onSuccess();
  } catch (ex) {
    showToast({
      icon: 'error',
      title: 'Oops, something went wrong',
      subTitle: ex?.response?.data?.code || ex.message,
    });
  }
};

export const getDailyNews = async (query: any) => {
  const { data } = await apis.onlineTest.get(apiUrls.onlineTest.DAILY_LEARNING_NEWS, query);
  return data;
};

export const getDailyNewsDetail = async (id) => {
  const { data } = await apis.onlineTest.get(apiUrls.onlineTest.DAILY_LEARNING_NEWS_DETAIL, { id });
  return transformDetail(data);
};

export const getLandingDailyNewsDetail = async (id) => {
  const { data } = await apis.publicOnlineTestV2.get(
    apiPublicUrls.onlineTestV2.DAILY_LEARNING_NEWS_DETAIL,
    {
      id,
    }
  );
  return transformDetail(data);
};

export const createDailyNews = async (payload) => {
  const res = await apis.onlineTest.post(apiUrls.onlineTest.DAILY_LEARNING_NEWS, payload);
  return res.data;
};

export const updateDailyNews = async ({ id, payload }) => {
  const { data } = await apis.onlineTest.put(
    apiUrls.onlineTest.DAILY_LEARNING_NEWS_DETAIL,
    payload,
    {
      params: { id },
    }
  );
  return transformDetail(data);
};

export const deleteDailyNews = async (id) => {
  const { data } = await apis.onlineTest.delete(apiUrls.onlineTest.DAILY_LEARNING_NEWS_DETAIL, {
    id,
  });
  return data;
};

export const publishDailyNews = async ({ id, payload }) => {
  const { data } = await apis.onlineTest.put(
    apiUrls.onlineTest.PUBLISH_DAILY_LEARNING_NEWS_DETAIL,
    payload,
    {
      params: { id },
    }
  );
  return data;
};

export const saveDailyNews = async ({ id, payload, onSuccess = noop }) => {
  return processUpdateOrPublishDailyNews({
    id,
    payload,
    onSuccess,
    apiFn: updateDailyNews,
  });
};

export const publishProcessDailyNews = async ({ id, payload, onSuccess = noop }) => {
  return processUpdateOrPublishDailyNews({
    id,
    payload,
    onSuccess,
    apiFn: publishDailyNews,
  });
};

export const getPageManagementDailyNews = async (query: any) => {
  const { data } = await apis.pageManagement.get(apiUrls.onlineTest.DAILY_LEARNING_NEWS, query);
  return data;
};

export const getPageManagementDailyNewsDetail = async (id: any) => {
  const { data } = await apis.pageManagement.get(apiUrls.onlineTest.DAILY_LEARNING_NEWS_DETAIL, {
    id,
  });
  return data;
};

export const updateListingStatus = async (id: string, status: string) => {
  const { data } = await apis.pageManagement.put(
    apiUrls.pageManagement.UPDATE_DAILY_NEWS_LISTING_STATUS,
    {},
    {
      params: { id, status },
    }
  );
  return data;
};

export const getLatestNewsList = async (fetchMode: 'TOP_3_LATEST' | 'TOP_5_LATEST') => {
  const { data } = await apis.publicPageManagement.get<components['schemas']['NewsListDTO']>(
    apiPublicUrls.pageManagement.GET_LATEST_NEWS_LIST,
    { fetchMode }
  );
  return data;
};
export const getPopularNewsList = async (params) => {
  const { data } = await apis.publicPageManagement.get<components['schemas']['NewsListDTO']>(
    apiPublicUrls.pageManagement.GET_POPULAR_NEWS_LIST,
    params
  );
  return data;
};

export const getRecentNewsList = async (params) => {
  const { data } = await apis.publicPageManagement.get<components['schemas']['PageNewsDTO']>(
    apiPublicUrls.pageManagement.GET_RECENT_NEWS_LIST,
    params
  );
  return data;
};

export const getActiveStories = async (params?: any) => {
  const { data } = await apis.publicOnlineTestV2.get<
    onlineTestDtoComponents['schemas']['ActiveStoriesDTO']
  >(apiPublicUrls.onlineTestV2.GET_ACTIVE_STORIES, {}, params);
  return data;
};

export const getActiveStoriesM2m = async (params?: any) => {
  const { data } = await apis.onlineTestM2M.get<
    onlineTestDtoComponents['schemas']['ActiveStoriesDTO']
  >(apiUrls.onlineTest.GET_ACTIVE_STORIES, {}, params);
  return data;
};

export const getActiveRiddle = async (params?: any) => {
  const { data } = await apis.publicOnlineTestV2.get(
    apiPublicUrls.onlineTestV2.GET_ACTIVE_RIDDLE,
    params
  );
  return data;
};

export const getActiveFact = async (params?: any) => {
  const { data } = await apis.publicOnlineTestV2.get(
    apiPublicUrls.onlineTestV2.GET_ACTIVE_FACT,
    params
  );
  return data;
};

export const getActiveVocab = async (params?: any) => {
  const { data } = await apis.publicOnlineTestV2.get(
    apiPublicUrls.onlineTestV2.GET_ACTIVE_VOCAB,
    params
  );
  return data;
};
